import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { Box, Text } from "@saleor/macaw-ui-next";
import { ReactNode } from "react";
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl";

import { labelPrintingSection } from "../../urls";
import { helpMessages as messages } from "./messages";

interface HelpSection {
  id: string;
  title: MessageDescriptor;
  steps: MessageDescriptor[];
}

interface HelpGroup {
  id: string;
  title: MessageDescriptor;
  sections: HelpSection[];
}

interface Machine {
  id: string;
  // Model names aren't translated.
  name: string;
  subtitle: MessageDescriptor;
  groups: HelpGroup[];
}

// One entry per printer; each printer's tips are grouped by when you need them.
const MACHINES: Machine[] = [
  {
    id: "zebra-gc420d",
    name: "Zebra GC420d",
    subtitle: messages.zebraSubtitle,
    groups: [
      {
        id: "setup",
        title: messages.groupSetup,
        sections: [
          {
            id: "windows",
            title: messages.windowsTitle,
            steps: [
              messages.windowsStep1,
              messages.windowsStep2,
              messages.windowsStep3,
              messages.windowsStep4,
            ],
          },
          {
            id: "mac",
            title: messages.macTitle,
            steps: [messages.macStep1, messages.macStep2],
          },
        ],
      },
      {
        id: "printing",
        title: messages.groupPrinting,
        sections: [
          {
            id: "dashboard",
            title: messages.chromeTitle,
            steps: [messages.chromeStep1, messages.chromeStep2, messages.chromeStep3],
          },
          {
            id: "calibrate",
            title: messages.calibrateTitle,
            steps: [messages.calibrateStep1, messages.calibrateStep2],
          },
        ],
      },
      {
        id: "trouble",
        title: messages.groupTrouble,
        sections: [
          {
            id: "faded",
            title: messages.fadedTitle,
            steps: [
              messages.fadedStep1,
              messages.fadedStep2,
              messages.fadedStep3,
              messages.fadedStep4,
            ],
          },
          {
            id: "misaligned",
            title: messages.troubleTitle,
            steps: [messages.troubleStep1, messages.troubleStep2, messages.troubleStep3],
          },
        ],
      },
    ],
  },
];

const bold = (chunks: ReactNode[]) => <strong>{chunks}</strong>;

const HelpCard = ({ section }: { section: HelpSection }) => (
  <DashboardCard
    id={section.id}
    borderWidth={1}
    borderStyle="solid"
    borderColor="default1"
    borderRadius={4}
    paddingBottom={4}
    __height="100%"
  >
    <DashboardCard.Header>
      <DashboardCard.Title>
        <FormattedMessage {...section.title} />
      </DashboardCard.Title>
    </DashboardCard.Header>
    <DashboardCard.Content>
      <Box as="ol" margin={0} padding={0} display="flex" flexDirection="column" gap={3}>
        {section.steps.map((step, index) => (
          <Box
            as="li"
            key={step.id}
            display="grid"
            gap={3}
            alignItems="start"
            __gridTemplateColumns="24px minmax(0, 1fr)"
            style={{ listStyle: "none" }}
          >
            <Text
              size={1}
              fontWeight="bold"
              display="grid"
              placeItems="center"
              borderRadius="100%"
              backgroundColor="default2"
              borderWidth={1}
              borderStyle="solid"
              borderColor="default1"
              __width="24px"
              __height="24px"
              aria-hidden
            >
              {index + 1}
            </Text>
            <Text size={3} style={{ lineHeight: 1.55, paddingTop: 1 }}>
              <FormattedMessage {...step} values={{ b: bold }} />
            </Text>
          </Box>
        ))}
      </Box>
    </DashboardCard.Content>
  </DashboardCard>
);

// Printer set-up tips for label printing, one section per printer model,
// reachable from the label page's header and from the paper settings when a
// roll is selected.
export const LabelPrintingHelp = () => {
  const intl = useIntl();

  return (
    <ListPageLayout>
      <TopNav href={labelPrintingSection} title={intl.formatMessage(messages.title)} />
      <Box
        display="flex"
        flexDirection="column"
        gap={8}
        paddingX={6}
        paddingY={5}
        data-test-id="label-printing-help"
      >
        {MACHINES.map(machine => (
          <Box
            as="section"
            key={machine.id}
            id={machine.id}
            display="flex"
            flexDirection="column"
            gap={6}
          >
            <Box
              display="flex"
              flexDirection="column"
              gap={1}
              paddingBottom={3}
              borderBottomWidth={1}
              borderBottomStyle="solid"
              borderColor="default1"
            >
              <Text as="h2" size={8} fontWeight="bold">
                {machine.name}
              </Text>
              <Text size={3} color="default2">
                <FormattedMessage {...machine.subtitle} />
              </Text>
            </Box>
            {machine.groups.map(group => (
              <Box key={group.id} display="flex" flexDirection="column" gap={3}>
                <Text
                  as="h3"
                  size={2}
                  fontWeight="bold"
                  color="default2"
                  textTransform="uppercase"
                  style={{ letterSpacing: "0.04em" }}
                >
                  <FormattedMessage {...group.title} />
                </Text>
                <Box
                  display="grid"
                  gap={4}
                  alignItems="start"
                  __gridTemplateColumns="repeat(auto-fit, minmax(min(100%, 440px), 1fr))"
                >
                  {group.sections.map(section => (
                    <HelpCard key={section.id} section={section} />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </ListPageLayout>
  );
};
